import { type FC, memo, useState } from "react";
import { Minus, Plus } from "lucide-react";

import SSNInput from "@/shared/components/SSNInput";
import { AddressInput } from "@/shared/components/ui/address-input";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { Label } from "@/shared/components/ui/label";

import type { FormField } from "@/shared/types/estateEssentials";

import { getOptionValue, getOptionLabel } from "../utils/formDisplayUtils";

interface FormFieldInputProps {
  field: FormField;
  value: string;
  onChange?: (value: string) => void;
  onFieldChange?: (key: string, value: string) => void;
  fieldKey?: string;
  error?: boolean;
  attention?: boolean;
  portalContainer?: HTMLElement | null;
}

const errorClass = "border-amber-400 bg-amber-50/50";
const attentionClass = "border-amber-400 bg-amber-50/50";

export const FormFieldInput: FC<FormFieldInputProps> = memo(
  ({
    field,
    value,
    onChange: onChangeDirect,
    onFieldChange,
    fieldKey,
    error,
    attention,
    portalContainer,
  }) => {
    const [ssnVisible, setSsnVisible] = useState(false);

    const onChange =
      onChangeDirect ?? ((val: string) => onFieldChange?.(fieldKey ?? field.key, val));

    const stateClass = error ? errorClass : attention ? attentionClass : undefined;

    if (field.type === "select" && field.options) {
      return (
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger className={stateClass}>
            <SelectValue placeholder={field.placeholder ?? `Select ${field.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={getOptionValue(opt)} value={getOptionValue(opt)}>
                {getOptionLabel(opt)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (field.type === "textarea") {
      return (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={stateClass}
        />
      );
    }

    if (field.type === "date") {
      return (
        <DatePicker
          dateString={value}
          onDateStringChange={(dateStr) => onChange(dateStr ?? "")}
          onChange={() => {}}
          placeholder={field.placeholder ?? "Select date"}
          className={stateClass}
        />
      );
    }

    if (field.type === "ssn") {
      return (
        <SSNInput
          valueFormatted={value}
          onChangeFormatted={onChange}
          ssnVisible={ssnVisible}
          toggleVisible={() => setSsnVisible((prev) => !prev)}
          isDetailMissing={false}
          className={stateClass}
        />
      );
    }

    if (field.type === "radio" && field.options) {
      return (
        <RadioGroup
          value={value}
          onValueChange={onChange}
          className={`flex flex-wrap items-center gap-x-4 gap-y-2 pt-1 ${stateClass ? `rounded-lg px-3 py-2 border ${stateClass}` : ""}`}
        >
          {field.options.map((opt) => {
            const optValue = getOptionValue(opt);
            const optLabel = getOptionLabel(opt);
            return (
              <div key={optValue} className="flex items-center gap-2">
                <RadioGroupItem value={optValue} id={`${field.key}-${optValue}`} />
                <Label
                  htmlFor={`${field.key}-${optValue}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {optLabel}
                </Label>
              </div>
            );
          })}
        </RadioGroup>
      );
    }

    if (field.type === "stepper") {
      const numValue = Math.max(1, parseInt(value, 10) || 1);
      return (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-8 shrink-0"
            onClick={() => onChange(String(Math.max(1, numValue - 1)))}
            disabled={numValue <= 1}
          >
            <Minus className="size-3.5" />
          </Button>
          <span className="text-sm font-medium w-4 text-center">{numValue}</span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-8 shrink-0"
            onClick={() => onChange(String(numValue + 1))}
          >
            <Plus className="size-3.5" />
          </Button>
        </div>
      );
    }

    if (field.type === "numeric") {
      return (
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={stateClass}
        />
      );
    }

    if (field.type === "address") {
      return (
        <AddressInput
          value={value}
          onChange={(val) => onChange(val)}
          placeholder={field.placeholder ?? "Search for an address..."}
          className={stateClass}
          portalContainer={portalContainer}
        />
      );
    }

    return (
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className={stateClass}
      />
    );
  },
);

FormFieldInput.displayName = "FormFieldInput";
